import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, ArrowUpRight, Check, Loader2, MessageCircle,
  Heart, Sparkles, Church, Crown, Flame, MapPin, Users, Clock,
  ChevronDown, ChevronUp, Phone, Wand2, Utensils,
  Camera, Car, PartyPopper, Bed, Info,
} from "lucide-react";
import {
  WEDDING_COLLECTIONS, WEDDING_HERO_IMAGE, findByPackageId, estimateRange,
  inr, ILLUSTRATIVE_EXPERIENCES,
} from "@/lib/weddingPackages";
import { BRAND, whatsappLink } from "@/lib/brand";
import { submitEnquiry } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const ICONS = { heart: Heart, sparkles: Sparkles, temple: Church, crown: Crown, om: Flame };

const PORTAL_TABS = [
  { k: "stay", label: "Stays", to: "/book" },
  { k: "yatra", label: "Yatra", to: "/book/yatra" },
  { k: "wedding", label: "Weddings", to: "/book/wedding" },
  { k: "anniversary", label: "Anniversary", to: "/book/anniversary" },
  { k: "event", label: "Events", to: "/book/event" },
  { k: "transport", label: "Transport", to: "/book/transport" },
];

// ============================================================================
export default function BookWedding() {
  const [params, setParams] = useSearchParams();
  const pkgId = params.get("p");
  const found = useMemo(() => (pkgId ? findByPackageId(pkgId) : null), [pkgId]);

  const nav = useNavigate();
  const clearAll = () => setParams({});
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [pkgId]);

  return (
    <div className="bg-kc min-h-screen" data-testid="portal-wedding">
      <section className="relative pt-32 md:pt-40 pb-14 md:pb-20 bg-black-lux text-[#F8F6F2] overflow-hidden">
        <img src={found?.collection.image || WEDDING_HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,11,12,0.7), rgba(11,11,12,0.95))" }} />
        <div className="relative max-w-[1500px] mx-auto px-6 md:px-12">
          <PortalSwitch current="wedding" />

          <nav className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-[#F8F6F2]/60 flex-wrap">
            <button onClick={() => nav("/")} className="hover:text-gold transition-colors" data-testid="crumb-home">Home</button>
            <span className="opacity-40">/</span>
            <button onClick={clearAll} className={!found ? "text-gold" : "hover:text-gold transition-colors"} data-testid="crumb-collections">Collections</button>
            {found && (<>
              <span className="opacity-40">/</span>
              <span className="text-gold">{found.tier.name}</span>
            </>)}
          </nav>

          <div className="mt-10 max-w-4xl">
            {!found ? (<>
              <div className="overline text-gold">Triyuginarayan Wedding Collection</div>
              <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
                Where love began, <br /><span className="italic text-gold">love returns.</span>
              </h1>
              <p className="mt-6 text-[15px] md:text-[16px] leading-[1.8] text-[#F8F6F2]/75 max-w-2xl">
                Nineteen curated wedding packages across seven collections — from a sacred boutique ceremony for twenty at Triyuginarayan Temple, to a full royal celebration for three hundred beside the Mandakini. Every quote begins here.
              </p>
            </>) : (<>
              <div className="overline text-gold">{found.collection.name}</div>
              <h1 className="font-serif-display text-4xl md:text-6xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
                {found.tier.name}
              </h1>
              <p className="mt-4 text-[#F8F6F2]/70 text-[14px] tracking-wide">{found.collection.guestRange} · {found.tier.note}</p>
              <div className="mt-6 flex items-baseline gap-3">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Starting From</div>
                <div className="font-serif-display text-3xl text-[#F8F6F2]">{inr(found.tier.startingFrom)}</div>
              </div>
            </>)}
          </div>
        </div>
      </section>

      {!found ? <CollectionGrid onOpen={(id) => setParams({ p: id })} /> : <PackageDetail collection={found.collection} tier={found.tier} onBack={clearAll} />}
    </div>
  );
}

// ============================================================================
const PortalSwitch = ({ current }) => (
  <div className="flex flex-wrap gap-1 border border-[#D4AF37]/25 p-1 self-start w-fit" data-testid="portal-switch">
    {PORTAL_TABS.map(t => (
      <Link key={t.k} to={t.to} className={`px-4 py-2 text-[10px] uppercase tracking-[0.24em] transition-colors ${current === t.k ? "bg-gold text-[#0F0F0F]" : "text-[#F8F6F2]/70 hover:text-gold"}`} data-testid={`portal-tab-${t.k}`}>
        {t.label}
      </Link>
    ))}
  </div>
);

// ============================================================================
// STEP 1 · COLLECTION GRID (7 collections shown as hero cards, tiers within)
// ============================================================================
const CollectionGrid = ({ onOpen }) => (
  <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-16 md:py-24 space-y-24" data-testid="wedding-collections">
    {WEDDING_COLLECTIONS.map((c, ci) => (
      <CollectionBlock key={c.id} collection={c} index={ci} onOpen={onOpen} />
    ))}
    <WhyKCBlock />
  </section>
);

const CollectionBlock = ({ collection, index, onOpen }) => {
  const Icon = ICONS[collection.icon] || Heart;
  return (
    <div data-testid={`collection-${collection.id}`}>
      <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 items-end mb-10">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold">
              <Icon size={18} strokeWidth={1.4} />
            </div>
            <div className="overline no-line">Collection {String(index + 1).padStart(2, "0")}</div>
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
            {collection.name}
          </h2>
          <div className="mt-3 text-[11px] uppercase tracking-[0.28em] text-gold">{collection.guestRange} · {collection.tagline}</div>
        </div>
        <p className="lg:col-span-5 text-ink-soft text-[14.5px] leading-[1.75]">{collection.overview}</p>
      </Reveal>

      <div className="relative h-[280px] md:h-[380px] overflow-hidden mb-6" data-testid={`collection-image-${collection.id}`}>
        <img src={collection.image} alt={collection.name} loading="lazy" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,11,12,0.1) 0%, rgba(11,11,12,0.8) 100%)" }} />
        <div className="absolute bottom-6 left-6 right-6 text-[#F8F6F2]">
          <div className="font-serif-display text-2xl md:text-3xl font-light italic text-gold max-w-3xl leading-tight">"{collection.heroLine}"</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {collection.tiers.map((t, i) => (
          <Reveal key={t.id} delay={i * 60}>
            <button onClick={() => onOpen(t.id)} className={`group w-full text-left p-6 md:p-8 border transition-all h-full flex flex-col ${t.featured ? "bg-black-lux text-[#F8F6F2] border-gold/60 hover:border-gold" : "bg-surface border-kc hover:border-gold/50 hover:bg-cream"}`} data-testid={`tier-${t.id}`}>
              <div className="flex items-center justify-between gap-4">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Tier {String(i + 1).padStart(2, "0")}</div>
                {t.featured && <span className="text-[9px] uppercase tracking-[0.28em] bg-gold text-[#0F0F0F] px-2 py-0.5">Signature</span>}
              </div>
              <div className="font-serif-display text-2xl mt-5 leading-tight font-light">{t.name}</div>
              <div className={`mt-2 text-[12.5px] leading-[1.6] flex-1 ${t.featured ? "text-[#F8F6F2]/60" : "text-ink-mute"}`}>{t.note}</div>
              <div className={`mt-6 pt-5 border-t flex items-baseline justify-between gap-3 ${t.featured ? "border-[#D4AF37]/30" : "border-kc"}`}>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-gold">{t.startingFrom ? "Starting From" : "Custom"}</div>
                  <div className="font-serif-display text-xl mt-1">{inr(t.startingFrom)}</div>
                </div>
                <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] ${t.featured ? "text-gold" : "text-gold"}`}>
                  Explore <ArrowUpRight size={12} />
                </span>
              </div>
            </button>
          </Reveal>
        ))}
      </div>
    </div>
  );
};

const WhyKCBlock = () => (
  <Reveal className="bg-black-lux text-[#F8F6F2] px-8 md:px-14 py-14 md:py-20 border-t-2 border-gold/40" data-testid="why-kc-wedding">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
      <div className="md:col-span-5">
        <div className="overline no-line text-gold">Why THE KC INDIA</div>
        <h3 className="font-serif-display text-3xl md:text-4xl mt-4 font-light leading-tight">The wedding-style hospitality <span className="italic text-gold">runs in our blood.</span></h3>
      </div>
      <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
        {[
          { title: "Temple Access", note: "Direct coordination with the Triyuginarayan Committee. Not everyone has this." },
          { title: "Full Ownership", note: "One director, one contract, one accountable team. Zero fragmented vendors." },
          { title: "Trained Team", note: "60+ full-time staff — chefs, pandits, drivers, coordinators. No outsourcing." },
          { title: "Local Roots",   note: "Three generations in the Kedarnath valley. We know every corner, every family." },
        ].map((w, i) => (
          <div key={i} className="border-t border-gold/25 pt-5">
            <div className="text-[10px] uppercase tracking-[0.28em] text-gold">№ {String(i + 1).padStart(2, "0")}</div>
            <div className="font-serif-display text-xl mt-2">{w.title}</div>
            <div className="text-[13px] text-[#F8F6F2]/65 mt-2 leading-relaxed">{w.note}</div>
          </div>
        ))}
      </div>
    </div>
  </Reveal>
);

// ============================================================================
// STEP 2 · PACKAGE DETAIL VIEW
// ============================================================================
const PackageDetail = ({ collection, tier, onBack }) => (
  <>
    <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-14 md:py-20">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-ink-soft hover:text-gold transition-colors" data-testid="back-to-collections">
        <ArrowLeft size={13} /> Back to Collections
      </button>
    </section>

    <HighlightsBlock collection={collection} tier={tier} />
    <IncludedBlock collection={collection} />
    <ItineraryBlock collection={collection} />
    <ServicesBlock collection={collection} />
    <GalleryBlock collection={collection} />
    <EstimatorBlock collection={collection} tier={tier} />
    <ReviewsBlock />
    <FAQBlock collection={collection} />
    <FinalCTABlock collection={collection} tier={tier} />
  </>
);

// ----- Highlights ---------
const HighlightsBlock = ({ collection, tier }) => (
  <section className="max-w-[1500px] mx-auto px-6 md:px-12 pb-14 md:pb-20" data-testid="pkg-highlights">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <div className="overline">Package Highlights</div>
        <h2 className="font-serif-display text-4xl md:text-5xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
          The essence of <br /><span className="italic text-gold">{tier.name}</span>
        </h2>
        <div className="mt-6 text-[14px] text-ink-soft leading-[1.75] max-w-md">{collection.overview}</div>

        <div className="mt-10 space-y-4">
          <MetaRow icon={Users}  label="Guests"   value={collection.guestRange} />
          <MetaRow icon={MapPin} label="Venue"    value="Triyuginarayan Temple · Sersi · Kedarnath valley" />
          <MetaRow icon={Clock}  label="Duration" value={`${collection.itinerary.length} day arc`} />
        </div>
      </div>

      <ul className="lg:col-span-7 space-y-4" data-testid="pkg-highlights-list">
        {collection.highlights.map((h, i) => (
          <Reveal key={i} delay={i * 60} className="flex items-start gap-4 pb-4 border-b border-kc">
            <div className="text-[10px] uppercase tracking-[0.24em] text-gold pt-1 flex-shrink-0">№ {String(i + 1).padStart(2, "0")}</div>
            <p className="text-[15px] leading-[1.75] text-ink">{h}</p>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

const MetaRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 border border-gold/40 flex items-center justify-center text-gold flex-shrink-0"><Icon size={16} /></div>
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-gold">{label}</div>
      <div className="text-[14px] mt-1 text-ink">{value}</div>
    </div>
  </div>
);

// ----- Included ---------
const IncludedBlock = ({ collection }) => (
  <section className="bg-cream section-pad" data-testid="pkg-included">
    <div className="max-w-[1500px] mx-auto px-6 md:px-12">
      <Reveal className="mb-12">
        <div className="overline">What's Included</div>
        <h2 className="font-serif-display text-3xl md:text-4xl mt-4 font-light leading-[1] tracking-[-0.02em]">
          Every essential, <span className="italic text-gold">held with care.</span>
        </h2>
      </Reveal>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        {collection.included.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-[14px] text-ink leading-[1.75] bg-surface border border-kc p-5" data-testid={`pkg-inc-${i}`}>
            <Check size={16} className="text-gold flex-shrink-0 mt-0.5" strokeWidth={2} /> {it}
          </li>
        ))}
      </ul>
    </div>
  </section>
);

// ----- Itinerary ---------
const ItineraryBlock = ({ collection }) => (
  <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-14 md:py-20" data-testid="pkg-itinerary">
    <Reveal className="mb-10">
      <div className="overline">Suggested Itinerary</div>
      <h2 className="font-serif-display text-3xl md:text-4xl mt-4 font-light leading-[1] tracking-[-0.02em]">
        The full arc <span className="italic text-gold">of your wedding.</span>
      </h2>
    </Reveal>
    <div className="space-y-4">
      {collection.itinerary.map((it, i) => (
        <Reveal key={i} delay={i * 60} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 border-l-2 border-gold/40 pl-6 pb-4">
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-gold">{it.day}</div>
          </div>
          <div className="md:col-span-10 text-[14.5px] text-ink leading-[1.75]">{it.label}</div>
        </Reveal>
      ))}
    </div>
  </section>
);

// ----- Services (accommodation · transport · décor · catering · photo) ------
const ServicesBlock = ({ collection }) => {
  const services = [
    { icon: Bed,       title: "Accommodation Options",     items: collection.accommodation, valueKey: "perNight", unit: "/ room / night" },
    { icon: Car,       title: "Transportation Options",    items: collection.transport,     valueKey: "flat",     unit: "· flat rate" },
    { icon: Wand2,     title: "Decoration Themes",         items: collection.decoration,    valueKey: "flat",     unit: "· includes setup" },
    { icon: Utensils,  title: "Catering Options",          items: collection.catering,      valueKey: "perGuest", unit: "/ guest" },
    { icon: Camera,    title: "Photography & Videography", items: collection.photography,   valueKey: "flat",     unit: "· full package" },
  ];
  return (
    <section className="bg-black-lux text-[#F8F6F2] section-pad" data-testid="pkg-services">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <Reveal className="mb-12">
          <div className="overline no-line text-gold">Configure Your Wedding</div>
          <h2 className="font-serif-display text-3xl md:text-4xl mt-4 font-light leading-[1] tracking-[-0.02em]">
            Choose the elements <br /><span className="italic text-gold">that shape your day.</span>
          </h2>
          <p className="mt-4 text-[13.5px] text-[#F8F6F2]/60 max-w-2xl">Every element listed here is available. Use the Live Estimator below to see how your selections shape the final range. Final quotation is confirmed on WhatsApp.</p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {services.map((s, i) => (
            <Reveal key={i} delay={i * 60} className="bg-[#0F0F0F] border border-[#D4AF37]/25 p-6 md:p-8">
              <s.icon size={20} className="text-gold" strokeWidth={1.3} />
              <div className="overline no-line mt-5 text-gold">{s.title}</div>
              <ul className="mt-5 space-y-3">
                {s.items.map((it, k) => (
                  <li key={k} className="flex items-start justify-between gap-3 text-[13px] text-[#F8F6F2]/85 leading-relaxed border-b border-[#D4AF37]/15 pb-3 last:border-0">
                    <div className="flex-1">{it.label}</div>
                    <div className="text-gold whitespace-nowrap text-[12px]">
                      {inr(it[s.valueKey])} <span className="text-[#F8F6F2]/40 text-[10px]">{s.unit}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}

          <Reveal delay={services.length * 60} className="bg-[#0F0F0F] border border-[#D4AF37]/25 p-6 md:p-8">
            <PartyPopper size={20} className="text-gold" strokeWidth={1.3} />
            <div className="overline no-line mt-5 text-gold">Optional Add-Ons</div>
            <ul className="mt-5 space-y-3">
              {collection.addons.map((a, k) => (
                <li key={k} className="flex items-start justify-between gap-3 text-[13px] text-[#F8F6F2]/85 leading-relaxed border-b border-[#D4AF37]/15 pb-3 last:border-0">
                  <div className="flex-1">{a.label}</div>
                  <div className="text-gold whitespace-nowrap text-[12px]">{inr(a.price)}</div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ----- Gallery ---------
const GalleryBlock = ({ collection }) => (
  <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-14 md:py-20" data-testid="pkg-gallery">
    <Reveal className="mb-10">
      <div className="overline">Gallery · Curated Vision</div>
      <h2 className="font-serif-display text-3xl md:text-4xl mt-4 font-light leading-[1] tracking-[-0.02em]">
        A glimpse of your <span className="italic text-gold">Himalayan celebration.</span>
      </h2>
    </Reveal>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
      {collection.gallery.map((src, i) => (
        <div key={i} className={`overflow-hidden aspect-square ${i === 0 || i === 5 ? "md:col-span-2 md:row-span-2 md:aspect-auto" : ""}`} data-testid={`pkg-gal-${i}`}>
          <img src={src} alt={`Wedding gallery ${i + 1}`} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[700ms]" />
        </div>
      ))}
    </div>
  </section>
);

// ============================================================================
// LIVE ESTIMATOR
// ============================================================================
const EstimatorBlock = ({ collection, tier }) => {
  const isCustom = collection.id === "custom";
  const [s, setS] = useState({
    guests: Math.min(collection.guestMax, Math.round(collection.guestMax * 0.6)),
    nights: 2,
    accommodation: collection.accommodation[1]?.key || collection.accommodation[0].key,
    transport:     collection.transport[1]?.key    || collection.transport[0].key,
    decoration:    collection.decoration[1]?.key   || collection.decoration[0].key,
    catering:      collection.catering[1]?.key     || collection.catering[0].key,
    photography:   collection.photography[1]?.key  || collection.photography[0].key,
    events: ["Wedding Pheras"],
    addons: [],
  });
  const upd = (k, v) => setS(f => ({ ...f, [k]: v }));
  const toggle = (k, v) => setS(f => ({ ...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v] }));
  const range = useMemo(() => estimateRange(collection, s), [collection, s]);

  const EVENTS = ["Haldi", "Mehendi", "Sangeet", "Wedding Pheras", "Reception"];

  return (
    <section id="estimator" className="bg-cream section-pad" data-testid="pkg-estimator">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <Reveal className="mb-10">
          <div className="overline">Live Estimator · Indicative Range</div>
          <h2 className="font-serif-display text-3xl md:text-5xl mt-4 font-light leading-[1] tracking-[-0.02em]">
            Shape your quote <span className="italic text-gold">in real time.</span>
          </h2>
          <p className="mt-4 text-[13.5px] text-ink-mute max-w-2xl">Adjust the sliders and selections below. The estimated range updates instantly. Your final quotation is confirmed on WhatsApp after our team reviews availability, dates, and any special requests.</p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* --- Controls --- */}
          <div className="lg:col-span-8 bg-surface border border-kc p-6 md:p-10 space-y-8" data-testid="estimator-controls">
            {!isCustom && (<>
              <NumberField label="Number of Guests" value={s.guests} min={2} max={collection.guestMax} onChange={v => upd("guests", v)} testid="est-guests" />
              <NumberField label="Nights" value={s.nights} min={1} max={5} onChange={v => upd("nights", v)} testid="est-nights" />

              <PillGroup label="Accommodation" items={collection.accommodation.map(a => ({ v: a.key, l: a.label }))} value={s.accommodation} onChange={v => upd("accommodation", v)} testid="est-acc" />
              <PillGroup label="Transportation" items={collection.transport.map(a => ({ v: a.key, l: a.label }))} value={s.transport} onChange={v => upd("transport", v)} testid="est-trs" />
              <PillGroup label="Decoration Theme" items={collection.decoration.map(a => ({ v: a.key, l: a.label }))} value={s.decoration} onChange={v => upd("decoration", v)} testid="est-dec" />
              <PillGroup label="Catering" items={collection.catering.map(a => ({ v: a.key, l: a.label }))} value={s.catering} onChange={v => upd("catering", v)} testid="est-cat" />
              <PillGroup label="Photography & Videography" items={collection.photography.map(a => ({ v: a.key, l: a.label }))} value={s.photography} onChange={v => upd("photography", v)} testid="est-pho" />

              <div>
                <label className="text-[10px] uppercase tracking-[0.28em] text-gold mb-4 block">Wedding Events</label>
                <div className="flex flex-wrap gap-2">
                  {EVENTS.map(e => (
                    <button key={e} type="button" onClick={() => toggle("events", e)} className={`text-[11px] uppercase tracking-[0.22em] px-4 py-2.5 border transition-colors ${s.events.includes(e) ? "bg-gold text-[#0F0F0F] border-gold" : "border-kc text-ink hover:border-gold"}`} data-testid={`est-event-${e.toLowerCase().replace(/\s/g,"-")}`}>
                      {s.events.includes(e) ? "✓ " : ""}{e}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.28em] text-gold mb-4 block">Optional Add-Ons</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {collection.addons.map(a => (
                    <button key={a.key} type="button" onClick={() => toggle("addons", a.key)} className={`text-left text-[12.5px] px-4 py-3 border transition-colors flex items-center justify-between gap-3 ${s.addons.includes(a.key) ? "bg-gold text-[#0F0F0F] border-gold" : "border-kc text-ink hover:border-gold"}`} data-testid={`est-addon-${a.key}`}>
                      <span className="flex items-center gap-2">{s.addons.includes(a.key) ? <Check size={12} /> : <span className="w-3 h-3 border border-current" />} {a.label}</span>
                      <span className={s.addons.includes(a.key) ? "" : "text-gold"}>{inr(a.price)}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>)}
            {isCustom && (
              <div className="p-6 border border-gold/40 bg-gold/5">
                <Info size={16} className="text-gold" />
                <div className="font-serif-display text-2xl mt-3 leading-tight">Customized weddings are quoted bespoke.</div>
                <p className="mt-3 text-[13.5px] text-ink-soft leading-[1.7]">Share your vision on WhatsApp — we'll build a detailed quotation within 24 hours. Every element of our other six collections is available and recombinable.</p>
              </div>
            )}
          </div>

          {/* --- Estimated Range Card --- */}
          <aside className="lg:col-span-4">
            <div className="bg-black-lux text-[#F8F6F2] p-8 md:p-10 border border-gold/25 lg:sticky lg:top-28" data-testid="estimator-range">
              <div className="overline no-line text-gold">Estimated Range</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#F8F6F2]/50 mt-1">Indicative · Final on WhatsApp</div>
              {range ? (<>
                <div className="mt-8 pb-6 border-b border-[#D4AF37]/25">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-gold">Between</div>
                  <div className="font-serif-display text-3xl md:text-4xl mt-2 leading-tight">{inr(range.low)}</div>
                  <div className="text-[11px] uppercase tracking-[0.28em] text-[#F8F6F2]/40 mt-1">and</div>
                  <div className="font-serif-display text-3xl md:text-4xl mt-1 leading-tight text-gold">{inr(range.high)}</div>
                </div>
                <div className="mt-5 space-y-2 text-[11.5px] text-[#F8F6F2]/60">
                  <div>Guests: <span className="text-[#F8F6F2]">{s.guests}</span></div>
                  <div>Nights: <span className="text-[#F8F6F2]">{s.nights}</span></div>
                  <div>Events: <span className="text-[#F8F6F2]">{s.events.length}</span></div>
                  <div>Add-ons: <span className="text-[#F8F6F2]">{s.addons.length}</span></div>
                </div>
              </>) : (
                <div className="mt-8 pb-6 border-b border-[#D4AF37]/25">
                  <div className="font-serif-display text-3xl mt-2 leading-tight text-gold">Get Custom Quote</div>
                  <div className="text-[12px] mt-2 text-[#F8F6F2]/60">Every element personalised.</div>
                </div>
              )}
              <div className="mt-8 space-y-3">
                <a href={buildEstimatorWhatsApp(collection, tier, s, range)} target="_blank" rel="noreferrer" className="btn-gold w-full justify-center" data-testid="estimator-whatsapp">
                  <MessageCircle size={14} /> Lock Quote on WhatsApp
                </a>
                <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className="btn-ghost-light w-full justify-center" data-testid="estimator-call">
                  <Phone size={13} /> Call · {BRAND.phone}
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

const buildEstimatorWhatsApp = (collection, tier, s, range) => {
  const rows = [
    `Namaste 🙏 I would like a detailed quote for ${tier.name} with THE KC INDIA.`, ``,
    `Collection: ${collection.name}`,
    `Tier: ${tier.name} (${inr(tier.startingFrom)} onwards)`,
    ``,
    `— My Selections —`,
    `Guests: ${s.guests}`,
    `Nights: ${s.nights}`,
    `Accommodation: ${collection.accommodation.find(x => x.key === s.accommodation)?.label}`,
    `Transportation: ${collection.transport.find(x => x.key === s.transport)?.label}`,
    `Decoration: ${collection.decoration.find(x => x.key === s.decoration)?.label}`,
    `Catering: ${collection.catering.find(x => x.key === s.catering)?.label}`,
    `Photography: ${collection.photography.find(x => x.key === s.photography)?.label}`,
    `Events: ${s.events.join(" · ")}`,
    s.addons.length ? `Add-Ons: ${s.addons.map(k => collection.addons.find(x => x.key === k)?.label).join(" · ")}` : null,
    ``,
    range ? `Estimated Range: ${inr(range.low)} – ${inr(range.high)}` : `Bespoke — awaiting your quotation.`,
  ];
  return whatsappLink(rows.filter(Boolean).join("\n"));
};

// ============================================================================
const NumberField = ({ label, value, min, max, onChange, testid }) => (
  <div>
    <div className="flex items-baseline justify-between mb-3">
      <label className="text-[10px] uppercase tracking-[0.28em] text-gold">{label}</label>
      <div className="font-serif-display text-2xl text-ink">{value}</div>
    </div>
    <input type="range" min={min} max={max} value={value} onChange={e => onChange(parseInt(e.target.value, 10))} className="w-full accent-gold" data-testid={testid} />
    <div className="flex justify-between text-[10px] uppercase tracking-[0.22em] text-ink-mute mt-1"><span>{min}</span><span>{max}</span></div>
  </div>
);

const PillGroup = ({ label, items, value, onChange, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.28em] text-gold mb-4 block">{label}</label>
    <div className="flex flex-wrap gap-2">
      {items.map(it => (
        <button key={it.v} type="button" onClick={() => onChange(it.v)} className={`text-[11.5px] tracking-wide px-4 py-2.5 border transition-colors ${value === it.v ? "bg-gold text-[#0F0F0F] border-gold" : "border-kc text-ink hover:border-gold"}`} data-testid={`${testid}-${it.v}`}>
          {it.l}
        </button>
      ))}
    </div>
  </div>
);

// ============================================================================
// REVIEWS (Illustrative Visions · marked clearly, no fake identities)
// ============================================================================
const ReviewsBlock = () => {
  const [realReviews, setRealReviews] = useState(null);
  useEffect(() => {
    // Fetch real Google Reviews when wired up. If none available, show illustrative cards.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reviews`;
    fetch(url).then(r => r.ok ? r.json() : null).then(d => {
      if (d && Array.isArray(d.reviews) && d.reviews.length > 0 && d.source === "google") {
        setRealReviews(d.reviews.slice(0, 4));
      }
    }).catch(() => {});
  }, []);

  return (
    <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-14 md:py-20" data-testid="pkg-reviews">
      <Reveal className="mb-10">
        <div className="overline">{realReviews ? "Live Google Reviews" : "Illustrative Vision · Sample Guest Experience"}</div>
        <h2 className="font-serif-display text-3xl md:text-4xl mt-4 font-light leading-[1] tracking-[-0.02em]">
          Voices from the <span className="italic text-gold">Kedarnath valley.</span>
        </h2>
        {!realReviews && (
          <p className="mt-4 text-[13px] text-ink-mute max-w-2xl leading-[1.7]">
            The cards below describe the kind of experience we hold ourselves to. They are <b>illustrative visions</b>, not real customer testimonials. Real Google Reviews will appear here automatically once our Places API is wired up.
          </p>
        )}
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
        {(realReviews || ILLUSTRATIVE_EXPERIENCES).map((r, i) => (
          <Reveal key={r.id || i} delay={i * 60} className="bg-surface border border-kc p-6 md:p-8" data-testid={`pkg-review-${i}`}>
            <div className="flex items-start gap-3 mb-4">
              <Sparkles size={16} className="text-gold flex-shrink-0" />
              <div className="text-[10px] uppercase tracking-[0.28em] text-gold">{realReviews ? "Google Review" : "Illustrative Vision"}</div>
            </div>
            <p className="font-serif-display text-[19px] leading-[1.55] italic text-ink">"{r.quote || r.text}"</p>
            <div className="mt-5 pt-4 border-t border-kc text-[11px] uppercase tracking-[0.28em] text-ink-mute">
              {r.context || r.author_name}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

// ============================================================================
// FAQ
// ============================================================================
const FAQBlock = ({ collection }) => {
  const [open, setOpen] = useState(0);
  return (
    <section className="bg-cream section-pad" data-testid="pkg-faq">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <Reveal className="mb-10">
          <div className="overline">Frequently Asked</div>
          <h2 className="font-serif-display text-3xl md:text-4xl mt-4 font-light leading-[1] tracking-[-0.02em]">
            Questions we hear <span className="italic text-gold">most often.</span>
          </h2>
        </Reveal>
        <div className="space-y-3">
          {collection.faqs.map((f, i) => (
            <div key={i} className={`border transition-colors ${open === i ? "border-gold bg-surface" : "border-kc bg-white"}`} data-testid={`pkg-faq-${i}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left">
                <div className="font-serif-display text-[17px] md:text-[19px] leading-tight text-ink">{f.q}</div>
                {open === i ? <ChevronUp size={16} className="text-gold flex-shrink-0" /> : <ChevronDown size={16} className="text-ink-mute flex-shrink-0" />}
              </button>
              {open === i && (
                <div className="px-6 pb-6 text-[14px] text-ink-soft leading-[1.75] border-t border-kc pt-5">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// FINAL CTA · BOOKING FORM
// ============================================================================
const FinalCTABlock = ({ collection, tier }) => {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", wedding_month: "", expected_guests: "", city: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) { toast.error("Please share your name and mobile number."); return; }
    setBusy(true);
    try {
      const payload = {
        type: "wedding", name: form.name, mobile: form.mobile, email: form.email || undefined,
        destination: collection.name, hotel: tier.name,
        wedding_month: form.wedding_month || undefined,
        expected_guests: form.expected_guests || undefined,
        city: form.city || undefined,
        message: form.message || undefined,
      };
      const saved = await submitEnquiry(payload);
      const wa = whatsappLink(`Namaste 🙏 I would like to plan my wedding — ${tier.name} from ${collection.name}. My family details are in the form. Please share the detailed quote.`);
      window.open(wa, "_blank", "noreferrer");
      setDone({ id: saved.id, url: wa });
      toast.success("Received. Namaste 🙏");
    } catch { toast.error("Something interrupted our line. Please WhatsApp us instead."); }
    finally { setBusy(false); }
  };

  if (done) return (
    <section className="bg-black-lux text-[#F8F6F2] section-pad" data-testid="pkg-cta-success">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-gold/10 border border-gold flex items-center justify-center text-gold"><Check size={22} strokeWidth={2} /></div>
        <div className="overline no-line mt-8 text-gold">Namaste</div>
        <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight">Your enquiry for <span className="italic text-gold">{tier.name}</span> has been received.</h2>
        <p className="mt-6 text-[#F8F6F2]/70 text-[15px] leading-relaxed max-w-lg mx-auto">A member of our wedding team will personally reach you on <b>{form.mobile}</b> within 10 minutes with a detailed quotation.</p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <a href={done.url} target="_blank" rel="noreferrer" className="btn-gold" data-testid="pkg-open-wa"><MessageCircle size={14} /> Continue on WhatsApp</a>
          <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className="btn-ghost-light" data-testid="pkg-call-now"><Phone size={13} /> Call Now</a>
          <Link to="/book/wedding" className="btn-ghost-light">Explore Other Collections</Link>
        </div>
        <div className="mt-12 pt-8 border-t border-[#D4AF37]/25 text-[11px] uppercase tracking-[0.28em] text-[#F8F6F2]/40">Enquiry ID · {done.id.slice(0, 8)}</div>
      </div>
    </section>
  );

  return (
    <section id="book" className="bg-black-lux text-[#F8F6F2] section-pad" data-testid="pkg-cta">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline no-line text-gold">Direct Enquiry</div>
            <h2 className="font-serif-display text-3xl md:text-4xl mt-4 font-light leading-tight">
              Reserve your <br /><span className="italic text-gold">wedding date.</span>
            </h2>
            <p className="mt-6 text-[14px] leading-[1.8] text-[#F8F6F2]/75">Share your dates and family plans. Our wedding director will personally call you within 10 minutes with a detailed quotation.</p>
            <div className="mt-10 pt-8 border-t border-[#D4AF37]/25 space-y-4">
              <a href={whatsappLink(`Namaste 🙏 I would like to plan my wedding — ${tier.name} from ${collection.name}.`)} target="_blank" rel="noreferrer" className="btn-gold w-full justify-center" data-testid="pkg-whatsapp">
                <MessageCircle size={14} /> WhatsApp Wedding Planner
              </a>
              <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className="btn-ghost-light w-full justify-center" data-testid="pkg-call">
                <Phone size={13} /> Call · {BRAND.phone}
              </a>
              <a href="#estimator" className="btn-ghost-light w-full justify-center" data-testid="pkg-goto-estimator">Get Custom Quote</a>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-8 bg-[#0F0F0F] border border-[#D4AF37]/25 p-8 md:p-12" data-testid="pkg-form">
            <div className="overline no-line text-gold">Guest Details</div>
            <h3 className="font-serif-display text-3xl mt-4 leading-tight">Begin Your Wedding Story</h3>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              <F label="Name"           v={form.name}            u={v => upd("name", v)}            required testid="pkg-name" />
              <F label="Mobile"         v={form.mobile}          u={v => upd("mobile", v)}          required testid="pkg-mobile" placeholder="+91 …" />
              <F label="Email · Optional" v={form.email}         u={v => upd("email", v)}           col={2} testid="pkg-email" placeholder="you@example.com" />
              <F label="Wedding Month"  v={form.wedding_month}   u={v => upd("wedding_month", v)}   testid="pkg-month" placeholder="e.g. October 2026" />
              <F label="Expected Guests" v={form.expected_guests} u={v => upd("expected_guests", v)} testid="pkg-guests" placeholder={`e.g. ${Math.round(collection.guestMax * 0.6)}`} />
              <F label="Your City · Optional" v={form.city}      u={v => upd("city", v)}            col={2} testid="pkg-city" placeholder="Delhi · Mumbai · New York" />
              <F label="Your Vision · Optional" v={form.message} u={v => upd("message", v)}         col={2} textarea testid="pkg-message" placeholder="e.g. muhurat preference, décor style, family traditions, dietary needs" />
            </div>
            <button type="submit" disabled={busy} className="mt-10 btn-gold w-full md:w-auto justify-center" data-testid="pkg-submit">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <>Reserve My Date · Open WhatsApp <ArrowUpRight size={14} /></>}
            </button>
            <p className="mt-5 text-[10px] uppercase tracking-[0.24em] text-[#F8F6F2]/40">By sharing your details, you consent to be contacted by THE KC INDIA on WhatsApp, phone and email.</p>
          </form>
        </div>
      </div>
    </section>
  );
};

const F = ({ label, v, u, required, col = 1, testid, textarea, placeholder }) => (
  <div className={col === 2 ? "md:col-span-2" : ""}>
    <span className="text-[10px] uppercase tracking-[0.28em] text-gold mb-2 block">{label}</span>
    {textarea
      ? <textarea rows={3} className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3.5 text-[13.5px] text-[#F8F6F2] placeholder:text-[#F8F6F2]/40 focus:outline-none focus:border-gold transition-colors" placeholder={placeholder} value={v} onChange={e => u(e.target.value)} data-testid={testid} />
      : <input type="text" className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3.5 text-[13.5px] text-[#F8F6F2] placeholder:text-[#F8F6F2]/40 focus:outline-none focus:border-gold transition-colors" placeholder={placeholder} value={v} onChange={e => u(e.target.value)} required={required} data-testid={testid} />
    }
  </div>
);
